import React, { useState, useEffect, useCallback } from 'react';
import Button from '../../../../composants/Button/Button';
import OtpVerification from './OtpVerification';
import SignatureCanvas from './SignatureCanvas';
import ContratPreview from './ContratPreview';
import CGVAccept from '../../../../composants/CGVAccept/CGVAccept';
import ValidationPopUp from '../../../../composants/ValidationPopUp/ValidationPopUp';
import './SignatureFlow.css';

interface SignataireInfo {
    nom: string;
    prenom: string;
    email: string;
    telephone?: string;
    fonction?: string;
}

interface SignatureFlowProps {
    candidatureId: number;
    onClose: () => void;
    onSigned: () => void;
}

type FlowState = 'loading' | 'ready' | 'signing' | 'success' | 'error';

export default function SignatureFlow({
    candidatureId,
    onClose,
    onSigned,
}: SignatureFlowProps) {
    const [flowState, setFlowState] = useState<FlowState>('loading');
    const [signataire, setSignataire] = useState<SignataireInfo | null>(null);
    const [telephoneValide, setTelephoneValide] = useState(false);
    const [otpToken, setOtpToken] = useState<string | null>(null);
    const [signatureData, setSignatureData] = useState<string | null>(null);
    const [signatureType, setSignatureType] = useState<'texte' | 'dessin'>('dessin');
    const [showCgu, setShowCgu] = useState(false);
    const [cguAccepted, setCguAccepted] = useState(false);
    const [loadingPdf, setLoadingPdf] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
    const [hasViewedContrat, setHasViewedContrat] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [editingName, setEditingName] = useState(false);
    const [editNomComplet, setEditNomComplet] = useState('');

    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');

    // Fetch signataire info on mount
    useEffect(() => {
        const fetchInfo = async () => {
            try {
                const res = await fetch(
                    `${apiUrl}/publications/ac27/contrats/${candidatureId}/signataire-info`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const data = await res.json();
                if (!res.ok) {
                    setErrorMessage(data.message || 'Impossible de récupérer les informations.');
                    setFlowState('error');
                    return;
                }
                setSignataire(data.signataire);
                setEditNomComplet(`${data.signataire.prenom || ''} ${data.signataire.nom || ''}`.trim());

                // Determine if phone is valid for SMS OTP
                const phone = data.signataire?.telephone || '';
                const normalizedPhone = phone.replace(/[\s.\-]/g, '');
                setTelephoneValide(/^(06|07)\d{8}$/.test(normalizedPhone));
                setFlowState('ready');
            } catch {
                setErrorMessage('Impossible de contacter le serveur.');
                setFlowState('error');
            }
        };
        fetchInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidatureId]);

    const handleOtpVerified = useCallback((tokenSig: string) => {
        setOtpToken(tokenSig);
    }, []);

    const handleSignatureChange = useCallback((base64: string | null, type: 'texte' | 'dessin') => {
        setSignatureData(base64);
        setSignatureType(type);
    }, []);

    const handleTelephoneUpdated = useCallback((newPhone: string) => {
        setSignataire(prev => prev ? { ...prev, telephone: newPhone } : prev);
        setTelephoneValide(true);
    }, []);

    const canSign = !!otpToken && !!signatureData;
    const hasProgress = otpSent;

    // Avertir si l'utilisateur tente de quitter/recharger la page
    useEffect(() => {
        if (!hasProgress || flowState === 'success') return;
        const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasProgress, flowState]);

    const handleClose = useCallback(() => {
        if (hasProgress && flowState !== 'success') {
            setShowLeaveConfirm(true);
        } else {
            onClose();
        }
    }, [hasProgress, flowState, onClose]);

    const handlePreviewContrat = async () => {
        if (loadingPdf) return;
        setLoadingPdf(true);
        try {
            const res = await fetch(
                `${apiUrl}/publications/ac27/contrats/${candidatureId}/telecharger`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!res.ok) return;
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener,noreferrer');
            setHasViewedContrat(true);
        } catch {
            // silently fail
        } finally {
            setLoadingPdf(false);
        }
    };

    const handleSign = async () => {
        if (!otpToken || !signatureData || !signataire) return;
        setFlowState('signing');
        setErrorMessage('');

        try {
            const res = await fetch(
                `${apiUrl}/publications/ac27/contrats/${candidatureId}/signer`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        token_signature: otpToken,
                        signature_image: signatureData,
                        signature_type: signatureType === 'texte' ? 'texte' : 'dessin',
                        signataire: {
                            nom: editNomComplet.split(' ').slice(1).join(' ') || editNomComplet,
                            prenom: editNomComplet.split(' ')[0] || '',
                            email: signataire.email,
                            fonction: signataire.fonction || '',
                        },
                    }),
                }
            );
            const data = await res.json();
            if (!res.ok) {
                setErrorMessage(data.message || 'Erreur lors de la signature.');
                setFlowState('ready');
                return;
            }
            setFlowState('success');
        } catch {
            setErrorMessage('Impossible de contacter le serveur.');
            setFlowState('ready');
        }
    };

    const handleSuccessClose = () => {
        onSigned();
        onClose();
    };

    return (
        <>
            <div className="signature-modal-backdrop" onClick={handleClose}>
                <div
                    className="signature-modal-panel"
                    onClick={(e) => e.stopPropagation()}
                >
                {/* Header */}
                <div className="signature-modal-header">
                    <h2>Accuser réception du contrat</h2>
                    <button className="modal-close-btn" onClick={handleClose} aria-label="Fermer">
                        ×
                    </button>
                </div>

                {/* Body */}
                <div className="signature-modal-body">
                    {flowState === 'loading' && (
                        <div className="signature-modal-loading">
                            <div className="spinner"></div>
                            <p>Chargement…</p>
                        </div>
                    )}

                    {flowState === 'error' && (
                        <div className="signature-modal-error">
                            <p>{errorMessage}</p>
                            <Button type="button" variant="secondary" value="Fermer" onClick={handleClose} />
                        </div>
                    )}

                    {(flowState === 'ready' || flowState === 'signing') && signataire && (
                        <>
                            {/* Section 1: OTP */}
                            <OtpVerification
                                candidatureId={candidatureId}
                                email={signataire.email}
                                telephone={signataire.telephone}
                                telephoneValide={telephoneValide}
                                onVerified={handleOtpVerified}
                                onTelephoneUpdated={handleTelephoneUpdated}
                                onOtpSent={() => setOtpSent(true)}
                            />

                            {/* Section 2: Identité signataire */}
                            <div className="signature-identity-section">
                                <p className="signature-identity-label">
                                    <strong>{editNomComplet}</strong> apparaîtra sur le contrat.
                                    {!editingName && (
                                        <button
                                            type="button"
                                            className="signature-identity-edit-btn"
                                            onClick={() => setEditingName(true)}
                                        >
                                            Modifier
                                        </button>
                                    )}
                                </p>
                                {editingName && (
                                    <div className="signature-identity-fields">
                                        <div className="signature-identity-field" style={{ flex: 2 }}>
                                            <label>Nom et prénom du signataire</label>
                                            <input
                                                type="text"
                                                value={editNomComplet}
                                                onChange={(e) => setEditNomComplet(e.target.value)}
                                                maxLength={80}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className="signature-identity-done-btn"
                                            onClick={() => setEditingName(false)}
                                        >
                                            Valider
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Separator */}
                            <div className="signature-modal-separator" />

                            {/* Section 3: Signature */}
                            <SignatureCanvas
                                signataireName={editNomComplet}
                                signataireFunction={signataire.fonction}
                                onSignatureChange={handleSignatureChange}
                            />

                            {/* Error */}
                            {errorMessage && (
                                <div className="signature-modal-inline-error">{errorMessage}</div>
                            )}

                            {/* Actions */}
                            <div className="signature-modal-actions">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    value={loadingPdf ? 'Chargement…' : 'VOIR LE CONTRAT'}
                                    size="small"
                                    onClick={handlePreviewContrat}
                                    disabled={loadingPdf}
                                />
                                <Button
                                    type="button"
                                    variant="primary"
                                    value={flowState === 'signing' ? 'Signature en cours…' : 'SIGNER LE CONTRAT'}
                                    onClick={() => setShowCgu(true)}
                                    disabled={!hasViewedContrat || !canSign || flowState === 'signing'}
                                />
                                {!hasViewedContrat && (
                                    <p className="signature-modal-hint">Veuillez d'abord voir le contrat avant de signer.</p>
                                )}
                            </div>
                        </>
                    )}

                    {flowState === 'success' && (
                        <div className="signature-modal-success">
                            <div className="signature-modal-success-icon">✓</div>
                            <h3>Contrat signé avec succès !</h3>
                            <p>Votre contrat a été signé. Un accusé de réception vous a été envoyé par email.</p>
                            <div className="signature-modal-success-actions">
                                <ContratPreview candidatureId={candidatureId} />
                                <Button
                                    type="button"
                                    variant="primary"
                                    value="Fermer"
                                    onClick={handleSuccessClose}
                                />
                            </div>
                        </div>
                    )}
                </div>
                </div>
            </div>

            {showCgu && (
                <CGVAccept
                    acceptCGV={cguAccepted}
                    setAcceptCGV={setCguAccepted}
                    isOpen={showCgu}
                    onClose={() => { setShowCgu(false); setCguAccepted(false); }}
                    onConfirm={() => { setShowCgu(false); setCguAccepted(false); handleSign(); }}
                />
            )}

            <ValidationPopUp
                isOpen={showLeaveConfirm}
                title="Signature en cours"
                text="Vous avez une signature en cours. Si vous quittez, vous devrez redemander un code de vérification. Êtes-vous sûr de vouloir quitter ?"
                cancelLabel="Rester"
                confirmLabel="Quitter"
                onClose={() => setShowLeaveConfirm(false)}
                onConfirm={() => { setShowLeaveConfirm(false); onClose(); }}
            />
        </>
    );
}
